/**
 * This Api class lets you define an API endpoint and methods to request
 * data and process it.
 *
 * See the [Backend API Integration](https://docs.infinite.red/ignite-cli/boilerplate/app/services/Services.md)
 * documentation for more details.
 */
import { ApiResponse, ApisauceInstance, create } from "apisauce"
import { AxiosRequestConfig } from "axios"
import { FeedbackRating } from "types/feedback"
import { TSentimentsData } from "types/sentiment"
import {
  MemberFactField,
  MemberFactFieldSchema,
  MemberType,
  MSKType,
  SponsorType,
} from "types/sponsor"
import TSTTEngine from "types/sttEngine"
import { TConversation, TSummarizedConversation } from "types/transcript"
import TTSEngine from "types/ttsEngine"
import { z } from "zod/v4"

import {
  getContextToken,
  isIntentionalContextReplacementInProgress,
  markIntentionalContextReplacement,
  setContextToken,
} from "src/utils/contextToken"
import type {
  ApiConfig,
  ChannelBrandData,
  ContentResourceSummary,
  ContextReactivateResponse,
  ContextRefreshResponse,
  ContextSwitchResponse,
  RoleInfoResponse,
  SponsorInfo,
  SponsorListResponse,
  TodoItem,
} from "./api.types"
import type { SessionErrorBody } from "./sessionErrors"
import type { UserRole } from "types/role"
import * as Types from "./api.types"
import { GeneralApiProblem, getGeneralApiProblem } from "./apiProblem"
import {
  classifySessionError,
  isContextTokenError as isContextTokenErrorBody,
  isSessionDeactivatedError as isSessionDeactivatedErrorBody,
  isSponsorInactiveError as isSponsorInactiveErrorBody,
} from "./sessionErrors"

/**
 * Configuring the apisauce instance.
 */

/**
 * Manages authenticated requests to the API. Requires getAuthToken for authentication.
 * For authentication endpoints (login, refresh), use AuthApi instead.
 */
export class Api {
  apisauce: ApisauceInstance
  config: ApiConfig
  getAuthToken: () => Promise<string | undefined>

  /**
   * Held during any session-mutating switch (sponsor or role) so that concurrent
   * requests cannot race against the cookie handshake and overwrite the correct
   * cookie with a stale one from an in-flight response.
   */
  private sessionSwitchPromise: Promise<void> | null = null
  private sessionSwitchResolve: (() => void) | null = null

  /**
   * Pending /context/switch (isLogin: true) promise for deduplication.
   *
   * Multiple parallel callers would each fire their own POST /context/switch
   * and race to overwrite the stored context_token. All subsequent callers
   * await the same promise instead of starting a competing request. Cleared
   * in switchContext once the in-flight call settles (success OR failure).
   */
  private pendingSwitchContextLoginPromise: Promise<{ kind: "ok" } | GeneralApiProblem> | null =
    null

  /**
   * Set up our API instance. Keep this lightweight!
   */
  constructor({
    config,
    onUnauthorized,
    onForbidden,
    onSessionDeactivated,
    onSponsorInactive,
    onContextTokenError,
    getAuthToken,
  }: {
    config: ApiConfig
    onUnauthorized: (requestConfig: AxiosRequestConfig, response: ApiResponse<any>) => void
    onForbidden: (requestConfig: AxiosRequestConfig, response: ApiResponse<any>) => void
    /**
     * 401 with SESSION_DEACTIVATED — exclusive session taken elsewhere.
     *
     * Return `true` if recovery succeeded (e.g. silent POST /context/refresh),
     * in which case the original request will be retried once with refreshed
     * headers. Return `false`/void if recovery failed or was suppressed; the
     * original 401 then propagates to the caller.
     */
    onSessionDeactivated?: (
      requestConfig: AxiosRequestConfig,
      response: ApiResponse<any>,
    ) => Promise<boolean | void> | boolean | void
    /**
     * 401 with `code === "INVALID_SPONSOR_CONTEXT"` or
     * `reason === "SPONSOR_NOT_ACTIVE_IN_ENVIRONMENT"` — the sponsor/channel was
     * deleted out from under the user. TERMINAL: there is no session to resume
     * and no token that will ever validate. The handler must clear local
     * session state and route the user to channel selection / login.
     *
     * Unlike the other recovery callbacks this has no return value: the
     * original request is NEVER retried (the sponsor is gone) and silent
     * recovery is never attempted (that is what creates the reactivate loop).
     */
    onSponsorInactive?: (
      requestConfig: AxiosRequestConfig,
      response: ApiResponse<any>,
    ) => Promise<void> | void
    /**
     * 401 with a code starting with `CONTEXT_TOKEN_` (e.g. CONTEXT_TOKEN_MISSING,
     * CONTEXT_TOKEN_INVALID). Recoverable token problem distinct from session
     * displacement — handler should run silent recovery without surfacing the
     * takeover dialog.
     *
     * Recovery uses POST /context/refresh only. If refresh fails, escalate to
     * session deactivation. There is no fallback to bootstrap (which requires a
     * sponsor_id and could displace another active tab).
     *
     * Return `true` on successful recovery so the original request can be
     * retried; return `false`/void to propagate the 401 to the caller.
     */
    onContextTokenError?: (
      requestConfig: AxiosRequestConfig,
      response: ApiResponse<any>,
    ) => Promise<boolean | void> | boolean | void
    getAuthToken: () => Promise<string | undefined>
  }) {
    this.config = config
    this.getAuthToken = getAuthToken

    this.apisauce = create({
      baseURL: this.config.url,
      timeout: this.config.timeout,
      headers: {
        Accept: "application/json",
      },
      // This is to allow non-origin API domains to set cookies.
      // And we have a lot of potentially used non-origin API domains.
      withCredentials: true,
    })

    // Add request interceptor to:
    // 1. Block all requests during a context switch until the switch completes, preventing
    //    concurrent responses from racing the new context token storage.
    //    /context/switch, /context/refresh, and /context/reactivate bypass the lock:
    //    all three are lifecycle endpoints; holding them would deadlock recovery.
    // 2. Hold token-less requests behind the in-flight login bootstrap promise
    //    (POST /context/switch with isLogin:true) so they pick up the fresh token
    //    once bootstrap settles instead of being rejected for missing X-Context-Token.
    // 3. Exclude /files/ URLs from sending credentials.
    //    FIXME: This is considered a temporary workaround to address C2-1547 & C2-1550
    //    We should consider passing the cookie to validate user access to the specific file.
    this.apisauce.axiosInstance.interceptors.request.use(async (config) => {
      const url = config.url ?? ""
      const isSessionSwitchEndpoint =
        url.includes("context/switch") ||
        url.includes("context/refresh") ||
        url.includes("context/reactivate")

      const isBootstrapBypass = isSessionSwitchEndpoint
      if (this.pendingSwitchContextLoginPromise && !isBootstrapBypass) {
        const headers = (config.headers ?? {}) as Record<string, unknown>
        const headerToken =
          (headers["X-Context-Token"] as string | undefined) ??
          (headers["x-context-token"] as string | undefined)
        if (!headerToken && !getContextToken()) {
          console.log("[API] request awaiting in-flight switchContext login bootstrap", { url })
          await this.pendingSwitchContextLoginPromise.catch(() => undefined)
          const fresh = getContextToken()
          if (fresh) {
            headers["X-Context-Token"] = fresh
            config.headers = headers as typeof config.headers
          }
        }
      }

      if (this.sessionSwitchPromise && !isSessionSwitchEndpoint) {
        await this.sessionSwitchPromise
        // The request config was built before the switch began, so its
        // X-Context-Token is stale. Stamp the freshly stored token now so
        // the request reaches the backend with the correct credentials.
        // This matters especially for cross-environment switches where the
        // old token is invalid on the new backend entirely.
        const fresh = getContextToken()
        if (fresh) {
          const hdrs = (config.headers ?? {}) as Record<string, unknown>
          hdrs["X-Context-Token"] = fresh
          config.headers = hdrs as typeof config.headers
        }
      }
      if (url.includes("/files/")) {
        config.withCredentials = false
      }
      return config
    })

    // Silent recovery + auto-retry.
    //
    // A 401 with SESSION_DEACTIVATED or CONTEXT_TOKEN_* is, in many cases,
    // recoverable: AuthContext's onSessionDeactivated / onContextTokenError
    // callbacks dispatch recovery. For CONTEXT_TOKEN_*: → /context/refresh.
    // For SESSION_DEACTIVATED: the takeover dialog / Resume Here path uses
    // /context/reactivate. When recovery succeeds the original request is
    // retried once with refreshed headers.
    //
    // We register at the axios layer (not via apisauce.addResponseTransform)
    // because apisauce response transforms cannot replace the response with a
    // retried response. The axios error interceptor can: returning a fulfilled
    // axios response from the rejection handler causes apisauce to normalize
    // and surface that response as if the original request had succeeded.
    //
    // Loop guards (mirrored in addResponseTransform below):
    //   * /context/switch, /context/refresh, /context/reactivate are exempt —
    //     they ARE the lifecycle endpoints; a failure must reach the caller.
    //   * `_contextRetry` flag prevents infinite retry loops.
    this.apisauce.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error?.config as
          | (AxiosRequestConfig & { _contextRetry?: boolean })
          | undefined
        const errorResponse = error?.response as
          | { status?: number; data?: SessionErrorBody }
          | undefined

        if (!originalRequest || !errorResponse || errorResponse.status !== 401) {
          return Promise.reject(error)
        }

        // Centralized matchers honor `code`, `resolution`, and (legacy)
        // `error` message strings. SESSION_DEACTIVATED wins over
        // CONTEXT_TOKEN_*. See sessionErrors.ts for the full matrix.
        const errorClass = classifySessionError(errorResponse.status, errorResponse.data)

        if (errorClass === "other") {
          return Promise.reject(error)
        }

        const url = originalRequest.url ?? ""

        // Step 1 (terminal): the sponsor/channel is gone. This is checked
        // BEFORE the recovery-endpoint / retry / intentional-replacement guards
        // because it is non-recoverable from every entry point — even a
        // /context/reactivate that returns INVALID_SPONSOR_CONTEXT must trigger
        // the terminal handler rather than silently failing. The original
        // request is never retried (there is no token that will ever validate).
        //
        // Exception: while an intentional rotation is in progress (the user is
        // actively switching to another sponsor), suppress the terminal handler
        // and let the rotation settle. A genuine channel-gone state will surface
        // again on the next request once the window closes.
        if (errorClass === "sponsor_inactive") {
          if (isIntentionalContextReplacementInProgress()) {
            console.log(
              "[API] 401 INVALID_SPONSOR_CONTEXT during intentional context replacement — suppressing terminal handler",
              { url },
            )
            return Promise.reject(error)
          }
          if (onSponsorInactive) {
            try {
              await onSponsorInactive(originalRequest, errorResponse as unknown as ApiResponse<any>)
            } catch (e) {
              console.warn("[API] onSponsorInactive callback threw:", e)
            }
          }
          return Promise.reject(error)
        }

        const isSessionDeactivated = errorClass === "session_deactivated"

        const isRecoveryEndpoint =
          url.includes("context/switch") ||
          url.includes("context/refresh") ||
          url.includes("context/reactivate")

        if (isRecoveryEndpoint) {
          return Promise.reject(error)
        }

        if (originalRequest._contextRetry) {
          return Promise.reject(error)
        }

        originalRequest._contextRetry = true

        // Intentional-replacement window. If the client is in the
        // middle of a known rotation (POST /context/switch, /context/refresh,
        // /context/reactivate) any 401 SESSION_DEACTIVATED /
        // CONTEXT_TOKEN_* on a parallel in-flight request is expected and
        // self-healing — the rotation will store a fresh token momentarily
        // (and may already have done so). Skip the recovery callback (which
        // would race with the rotation's own token storage) and just retry
        // once the rotation has settled. The retry path below awaits any
        // in-flight session-switch lock and refreshes headers from the
        // freshly stored token before replaying the request.
        const intentional = isIntentionalContextReplacementInProgress()

        if (!intentional) {
          const callback = isSessionDeactivated ? onSessionDeactivated : onContextTokenError

          if (!callback) {
            return Promise.reject(error)
          }

          let recovered = false
          try {
            recovered =
              (await callback(originalRequest, errorResponse as unknown as ApiResponse<any>)) ===
              true
          } catch (e) {
            console.warn("[API] silent recovery callback threw:", e)
            recovered = false
          }

          if (!recovered) {
            return Promise.reject(error)
          }
        } else {
          console.log(
            "[API] 401 during intentional context replacement — skipping callback, retrying after rotation settles",
            {
              url,
              errorClass,
              code: errorResponse.data?.code,
              resolution: errorResponse.data?.resolution,
            },
          )
        }

        // Recovery succeeded (or intentional rotation in progress). The
        // session-switch lock may have been acquired (or released) while the
        // callback / rotation was running; await it before we refresh headers
        // so the X-Context-Token we attach reflects the freshest stored value.
        if (this.sessionSwitchPromise) {
          await this.sessionSwitchPromise
        }

        const freshHeaders = await this.getHeaders()
        originalRequest.headers = {
          ...(originalRequest.headers ?? {}),
          ...freshHeaders,
        }

        return this.apisauce.axiosInstance.request(originalRequest)
      },
    )

    // Legacy 401/403 routing for cases NOT covered by the axios interceptor:
    //   * generic 401 (no SESSION_DEACTIVATED / CONTEXT_TOKEN_* code) → onUnauthorized
    //   * 401 with SESSION_DEACTIVATED / CONTEXT_TOKEN_* but no handler registered
    //     (defensive fallback so a misconfigured Api instance still signs the
    //     user out instead of silently swallowing the error)
    //   * 403 → onForbidden
    //
    // The lifecycle endpoints (/context/switch, /context/refresh, /context/reactivate)
    // are exempted from any 401 callback so a lifecycle failure returns directly to
    // its caller (AuthContext's recovery handler) without re-entering.
    this.apisauce.addResponseTransform((response) => {
      if (response.status === 401) {
        const requestConfig = response.config as AxiosRequestConfig
        const body = response.data as SessionErrorBody | undefined
        const isSponsorInactive = isSponsorInactiveErrorBody(response.status, body)
        const isSessionDeactivated = isSessionDeactivatedErrorBody(response.status, body)
        const isContextTokenError = isContextTokenErrorBody(response.status, body)
        const url = requestConfig.url ?? ""
        const isRecoveryEndpoint =
          url.includes("context/switch") ||
          url.includes("context/refresh") ||
          url.includes("context/reactivate")

        if (
          isRecoveryEndpoint &&
          (isSponsorInactive || isSessionDeactivated || isContextTokenError)
        ) {
          // Lifecycle endpoint failure — caller observes the error directly.
          // (For sponsor_inactive the terminal handler already fired in the
          // axios interceptor regardless of endpoint.)
          return
        }

        if (isSponsorInactive && onSponsorInactive) {
          // Terminal sponsor-gone state. Already invoked (and awaited) by the
          // axios response interceptor — do NOT fall through to onUnauthorized.
          return
        }

        if (isSessionDeactivated && onSessionDeactivated) {
          // Already invoked (and awaited) by the axios response interceptor.
          return
        }

        if (isContextTokenError && onContextTokenError) {
          // Already invoked (and awaited) by the axios response interceptor.
          return
        }

        // Either no special code, or a special code with no handler registered.
        // Treat as a generic auth failure and clear tokens.
        onUnauthorized(requestConfig, response)
      } else if (response.status === 403) {
        const requestConfig = response.config as AxiosRequestConfig
        onForbidden(requestConfig, response)
      }
    })
  }

  /**
   * Build the standard authenticated headers: Authorization + X-Context-Token.
   */
  async getHeaders() {
    const authToken = await this.getAuthToken()
    const contextToken = getContextToken()
    return {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      ...(contextToken ? { "X-Context-Token": contextToken } : {}),
    }
  }

  /**
   * Build Cognito-only headers (Authorization only, no X-Context-Token).
   * Used for endpoints that use Cognito-only auth:
   *   - POST /context/switch (login-bootstrap path, no prior context token)
   *   - GET /sponsors/sponsor-list
   */
  async getCognitoOnlyHeaders() {
    const authToken = await this.getAuthToken()
    return {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    }
  }

  // according to Gil Raz in https://sidekickwellness.atlassian.net/browse/C2-1387, this endpoint should be called on the standard api host as cleanup is required on the server.
  async logout(
    accessToken: string,
    refreshToken: string,
  ): Promise<{ kind: "ok" } | GeneralApiProblem> {
    console.log("[API] logout")
    const response: ApiResponse<{ message: string }> = await this.apisauce.post(
      `logout`,
      {
        access_token: accessToken,
        refresh_token: refreshToken,
      },
      {
        headers: await this.getHeaders(),
      },
    )
    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    return { kind: "ok" }
  }

  /**
   * Gets a list of conversation summaries.
   * @param page The 1-based page number to fetch. Omit or pass 1 for the first page.
   */
  async getConversationsSummaries(
    page?: number,
  ): Promise<
    { kind: "ok"; summaries: TSummarizedConversation[]; hasMore: boolean } | GeneralApiProblem
  > {
    console.log("[API] getConversationsSummaries", page ? `page=${page}` : "")
    const params: Record<string, unknown> = {}
    if (page !== undefined && page > 1) {
      params.page = page
    }
    const response: ApiResponse<any> = await this.apisauce.get(`conversations/summaries`, params, {
      headers: await this.getHeaders(),
    })

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    try {
      const rawData = response.data
      const summaries: TSummarizedConversation[] = rawData?.conversations ?? []
      // Use the server's has_more flag when present; otherwise assume more exist if we got any results.
      const hasMore: boolean = rawData?.has_more ?? rawData?.hasMore ?? summaries.length > 0
      return { kind: "ok", summaries, hasMore }
    } catch (e) {
      if (__DEV__ && e instanceof Error) {
        console.error(`Bad data: ${e.message}\n${response.data}`, e.stack)
      }
      return { kind: "bad-data", data: response }
    }
  }

  async closeCurrentConversation(): Promise<{ kind: "ok"; result: boolean } | GeneralApiProblem> {
    console.log("[API] closeCurrentConversation")
    const response: ApiResponse<any> = await this.apisauce.post(`conversations/close`, null, {
      headers: await this.getHeaders(),
    })

    if (!response.ok) {
      // 409 Conflict = no conversation to close (already closed or none open). Treat as success.
      if (response.status === 409) {
        return { kind: "ok", result: false }
      }
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    try {
      const rawData = response.data
      const result: boolean = (rawData?.result ?? "") === "closed"

      return { kind: "ok", result }
    } catch (e) {
      if (__DEV__ && e instanceof Error) {
        console.error(`Bad data: ${e.message}\n${response.data}`, e.stack)
      }
      return { kind: "bad-data", data: response }
    }
  }

  async getCurrentConversation(): Promise<
    { kind: "ok"; conversation: TConversation } | GeneralApiProblem
  > {
    console.log("[API] getCurrentConversation")
    const response: ApiResponse<any> = await this.apisauce.get(
      `conversations/current`,
      {},
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      if (__DEV__) {
        console.warn("[API] getCurrentConversation non-OK", {
          status: response.status,
          problem: response.problem,
        })
      }
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    try {
      const rawData = response.data
      const conversation = rawData ?? {}
      return { kind: "ok", conversation }
    } catch (e) {
      if (__DEV__ && e instanceof Error) {
        console.error(`Bad data: ${e.message}\n${response.data}`, e.stack)
      }
      return { kind: "bad-data", data: response }
    }
  }

  async getConversationById(
    id: string,
  ): Promise<{ kind: "ok"; conversation: TConversation } | GeneralApiProblem> {
    console.log(`[API] getConversationById(${id})`)
    const response: ApiResponse<any> = await this.apisauce.get(
      `conversations/${id}`,
      {},
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    try {
      const rawData = response.data
      const conversation = rawData ?? {}
      return { kind: "ok", conversation }
    } catch (e) {
      if (__DEV__ && e instanceof Error) {
        console.error(`Bad data: ${e.message}\n${response.data}`, e.stack)
      }
      return { kind: "bad-data", data: response }
    }
  }

  //
  // Lists
  //
  async getToDoList(): Promise<{ kind: "ok"; list: TodoItem[] } | GeneralApiProblem> {
    console.log("[API] getToDoList")

    const response: ApiResponse<any> = await this.apisauce.get(
      `todo-list/all`,
      {},
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    try {
      const rawData = response.data
      const list = rawData ?? []
      return { kind: "ok", list }
    } catch (e) {
      if (__DEV__ && e instanceof Error) {
        console.error(`Bad data: ${e.message}\n${response.data}`, e.stack)
      }
      return { kind: "bad-data", data: response }
    }
  }

  async clearToDoList(): Promise<{ kind: "ok" } | GeneralApiProblem> {
    console.log("[API] clearToDoList")
    // this should ideally be a POST request, not a GET.
    const response: ApiResponse<any> = await this.apisauce.get(
      `todo-list/clear`,
      {},
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    try {
      const rawData = response.data

      console.log("clear todo list response", rawData)
      // const list = rawData ?? {}

      // TODO: handle the response data

      return { kind: "ok" }
    } catch (e) {
      if (__DEV__ && e instanceof Error) {
        console.error(`Bad data: ${e.message}\n${response.data}`, e.stack)
      }
      return { kind: "bad-data", data: response }
    }
  }

  //
  // User
  //
  async getUserId(): Promise<{ kind: "ok"; userId: string } | GeneralApiProblem> {
    console.log("[API] getUserId")
    const response: ApiResponse<any> = await this.apisauce.get(
      `user-id`,
      {},
      { headers: await this.getHeaders() },
    )

    // the typical ways to die when calling an api
    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    // transform the data into the format we are expecting
    try {
      const rawData = response.data

      // This is where we transform the data into the shape we expect for our MST model.
      const userId = rawData ?? ""

      console.log("userId fetched", userId)

      return { kind: "ok", userId }
    } catch (e) {
      if (__DEV__ && e instanceof Error) {
        console.error(`Bad data: ${e.message}\n${response.data}`, e.stack)
      }
      return { kind: "bad-data", data: response }
    }
  }

  async getMember(): Promise<{ kind: "ok"; member: MemberType } | GeneralApiProblem> {
    console.log("[API] getMember")
    const response: ApiResponse<any> = await this.apisauce.get(
      `members/get-member`,
      {},
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    try {
      const rawData = response.data
      const member = rawData ?? {}
      return { kind: "ok", member }
    } catch (e) {
      if (__DEV__ && e instanceof Error) {
        console.error(`Bad data: ${e.message}\n${response.data}`, e.stack)
      }
      return { kind: "bad-data", data: response }
    }
  }

  async updateMember({
    mskId,
  }: {
    mskId: string
  }): Promise<{ kind: "ok"; detail: string } | GeneralApiProblem> {
    console.log("[API] updateMember")
    const response: ApiResponse<any> = await this.apisauce.post(
      `members/update-member`,
      {
        data: {
          mskId,
        },
      },
      {
        headers: {
          ...(await this.getHeaders()),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    const detail = response.data?.detail
    if (!detail) {
      return { kind: "bad-data", data: response }
    }
    return { kind: "ok", detail }
  }

  async getFactFields(): Promise<{ kind: "ok"; fields: MemberFactField[] } | GeneralApiProblem> {
    console.log("[API] getFactFields")
    const response: ApiResponse<any> = await this.apisauce.get(
      `facts/get_fact_fields`,
      {},
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    // FIXME: backend shouldn't return broken fields
    // Parse each field, warn and continue for unparseable fields
    const fieldsData = response.data
      .map((item: any, idx: number) => {
        // First check with strict to warn about extra/invalid fields
        const strictResult = MemberFactFieldSchema.strict().safeParse(item)
        if (!strictResult.success) {
          console.warn(`getFactFields: Schema mismatch - ${idx}`, z.formatError(strictResult.error))
        }
        // Then parse without strict to accept data with extra keys stripped
        const result = MemberFactFieldSchema.safeParse(item)
        if (result.success) {
          return result.data
        } else {
          console.warn(`getFactFields: Invalid fact field - ${idx}`, z.formatError(result.error))
          return null
        }
      })
      .filter((item: MemberFactField | null) => item !== null)

    return { kind: "ok", fields: fieldsData }
  }

  async updateMemberFacts(
    facts: Record<string, string>,
  ): Promise<{ kind: "ok"; detail: string } | GeneralApiProblem> {
    console.log("[API] updateMemberFacts", facts)
    const response: ApiResponse<any> = await this.apisauce.post(
      `members/update-member-facts`,
      { data: facts },
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    const detail = response.data?.detail
    if (!detail) {
      return { kind: "bad-data", data: response }
    }
    return { kind: "ok", detail }
  }

  // Mark: Config

  async getTTSAvailable(): Promise<{ kind: "ok"; ttsConfig: TTSEngine[] } | GeneralApiProblem> {
    console.log("[API] getTTSAvailable")
    const response: ApiResponse<any> = await this.apisauce.get(
      `tts/available`,
      {},
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    try {
      const rawData = response.data
      const ttsConfig = rawData ?? ""
      return { kind: "ok", ttsConfig }
    } catch (e) {
      if (__DEV__ && e instanceof Error) {
        console.error(`Bad data: ${e.message}\n${response.data}`, e.stack)
      }
      return { kind: "bad-data", data: response }
    }
  }

  async getSTTAvailable(): Promise<{ kind: "ok"; sttConfig: TSTTEngine[] } | GeneralApiProblem> {
    console.log("[API] getSTTAvailable")
    const response: ApiResponse<any> = await this.apisauce.get(
      `stt/available`,
      {},
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    try {
      const rawData = response.data
      const sttConfig = rawData ?? ""
      return { kind: "ok", sttConfig }
    } catch (e) {
      if (__DEV__ && e instanceof Error) {
        console.error(`Bad data: ${e.message}\n${response.data}`, e.stack)
      }
      return { kind: "bad-data", data: response }
    }
  }

  async getLLMsAvailable(): Promise<Types.LLMAvailableResponse | GeneralApiProblem> {
    console.log("[API] getLLMsAvailable")
    const response = await this.apisauce.get<Types.LLMConfig[]>(
      "/llm/available",
      {},
      {
        headers: await this.getHeaders(),
      },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    try {
      const llmConfig = response.data ?? []
      return { kind: "ok", llmConfig }
    } catch (e) {
      if (__DEV__ && e instanceof Error) {
        console.error(`Bad data: ${e.message}\n${response.data}`, e.stack)
      }
      return { kind: "bad-data", data: response }
    }
  }

  async getMSKTypes(): Promise<{ kind: "ok"; mskTypes: MSKType[] } | GeneralApiProblem> {
    console.log("[API] getMSKTypes")
    const response: ApiResponse<any> = await this.apisauce.get(
      `sponsors/msk-types`,
      {},
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    try {
      const rawData = response.data
      const mskTypes = rawData ?? []
      return { kind: "ok", mskTypes }
    } catch (e) {
      if (__DEV__ && e instanceof Error) {
        console.error(`Bad data: ${e.message}\n${response.data}`, e.stack)
      }
      return { kind: "bad-data", data: response }
    }
  }

  async getESKTypes(): Promise<{ kind: "ok"; eskTypes: SponsorType[] } | GeneralApiProblem> {
    console.log("[API] getESKTypes")
    const response: ApiResponse<any> = await this.apisauce.get(
      `sponsors/esk-types`,
      {},
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    try {
      const rawData = response.data
      const eskTypes = rawData ?? []
      return { kind: "ok", eskTypes }
    } catch (e) {
      if (__DEV__ && e instanceof Error) {
        console.error(`Bad data: ${e.message}\n${response.data}`, e.stack)
      }
      return { kind: "bad-data", data: response }
    }
  }

  async getBackendAPIVersion(): Promise<{ kind: "ok"; version?: string } | GeneralApiProblem> {
    console.log("[API] getBackendAPIVersion")
    const response: ApiResponse<string> = await this.apisauce.get(
      `get-backend-api-version`,
      {},
      { headers: await this.getHeaders() },
    )
    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }
    const version = response.data
    if (!version) {
      return { kind: "bad-data", data: response.data }
    }
    return { kind: "ok", version }
  }

  async getSentiment(): Promise<
    { kind: "ok"; sentimentData: TSentimentsData[] } | GeneralApiProblem
  > {
    const response: ApiResponse<any> = await this.apisauce.get(
      `sentiments`,
      {},
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    try {
      const rawData = response.data
      const sentimentData = rawData ?? []
      return { kind: "ok", sentimentData }
    } catch (e) {
      if (__DEV__ && e instanceof Error) {
        console.error(`Bad data: ${e.message}\n${response.data}`, e.stack)
      }
      return { kind: "bad-data", data: response }
    }
  }

  // MARK: CAUTION ZONE:
  async resetDemo(): Promise<{ kind: "ok"; result: boolean } | GeneralApiProblem> {
    console.log("[API] resetDemo")
    const response: ApiResponse<any> = await this.apisauce.get(
      `reset-demo`,
      {},
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    try {
      const rawData = response.data
      const result: boolean = (rawData?.result ?? "") === "closed"
      return { kind: "ok", result }
    } catch (e) {
      if (__DEV__ && e instanceof Error) {
        console.error(`Bad data: ${e.message}\n${response.data}`, e.stack)
      }
      return { kind: "bad-data", data: response }
    }
  }

  // Activity Tracking for Billing
  async reportActivity(
    _timestamp: number,
    activity: string,
    description?: string,
    conversationId?: string,
  ): Promise<{ kind: "ok"; result: boolean } | GeneralApiProblem> {
    const response: ApiResponse<any> = await this.apisauce.post(
      "activity-user-status",
      {
        data: {
          activity,
          description,
          custom_data: {
            conversation_id: conversationId,
          },
        },
      },
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    return { kind: "ok", result: true }
  }

  // Feedback
  async getAnnotationTags(): Promise<
    { kind: "ok"; tags: Types.AnnotationTag[] } | GeneralApiProblem
  > {
    console.log("[API] getAnnotationTags")
    const response: ApiResponse<Types.AnnotationTag[]> = await this.apisauce.get(
      `get-annotation-tags`,
      {},
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    try {
      const tags = response.data ?? []
      return { kind: "ok", tags }
    } catch (e) {
      if (__DEV__ && e instanceof Error) {
        console.error(`[API] getAnnotationTags - Bad data: ${e.message}\n${response.data}`, e.stack)
      }
      return { kind: "bad-data", data: response }
    }
  }

  async submitFeedback(
    message: string,
    messageId: string,
    comment: string,
    rating: FeedbackRating,
    appVersion?: string,
    tagIds?: string[],
  ): Promise<{ kind: "ok"; result: boolean } | GeneralApiProblem> {
    console.log("[API] submitFeedback", message, rating)
    const endpoint =
      rating === FeedbackRating.THUMBS_UP ? "message/thumbs-up" : "message/thumbs-down"

    const response: ApiResponse<any> = await this.apisauce.post(
      endpoint,
      {
        message,
        message_id: messageId,
        comment,
        action: rating === FeedbackRating.THUMBS_UP ? "up" : "down",
        ...(appVersion && { app_version: appVersion }),
        ...(tagIds && tagIds.length > 0 && { tag_ids: tagIds }),
      },
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    return { kind: "ok", result: true }
  }

  // Role management API methods

  /**
   * Get user role information and available roles
   */
  async getRoleInfo(): Promise<{ kind: "ok"; result: RoleInfoResponse } | GeneralApiProblem> {
    console.log("[API] getRoleInfo")
    const response: ApiResponse<RoleInfoResponse> = await this.apisauce.get(
      "role/info",
      {},
      {
        headers: await this.getHeaders(),
      },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    if (!response.data) {
      return { kind: "bad-data", data: response }
    }

    return { kind: "ok", result: response.data }
  }

  //
  // Sponsor Management
  //

  /**
   * Fetch the user's available sponsors. Uses Cognito-only auth (no X-Context-Token).
   * Used for the in-app sponsor switcher and as a fallback when the in-memory
   * pendingLoginSponsors list is not available (page reload before sponsor selection).
   */
  async getSponsorList(): Promise<{ kind: "ok"; sponsors: SponsorInfo[] } | GeneralApiProblem> {
    console.log("[API] getSponsorList")
    const response: ApiResponse<SponsorListResponse> = await this.apisauce.get(
      "sponsors/sponsor-list",
      {},
      {
        headers: await this.getCognitoOnlyHeaders(),
      },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    if (!response.data?.sponsors) {
      return { kind: "bad-data", data: response }
    }

    return { kind: "ok", sponsors: response.data.sponsors }
  }

  /**
   * Acquires the session-switch lock. All subsequent requests (except the
   * lifecycle endpoints) will be held in the request interceptor until
   * `completeSessionSwitch` is called.
   *
   * Must always be paired with a `completeSessionSwitch` call (e.g. in a finally block).
   */
  beginSessionSwitch() {
    if (this.sessionSwitchPromise) {
      console.warn("[API] beginSessionSwitch called while a switch is already in progress")
      return
    }
    this.sessionSwitchPromise = new Promise<void>((resolve) => {
      this.sessionSwitchResolve = resolve
    })
    console.log("[API] Session switch lock acquired")
  }

  /**
   * Releases the session-switch lock, unblocking any requests that were queued
   * in the interceptor while the switch was in progress.
   *
   * Idempotent: safe to call even when no lock is currently held, so it can be
   * used both as an explicit early release and as a finally-block safety net.
   */
  completeSessionSwitch() {
    if (!this.sessionSwitchPromise) return
    this.sessionSwitchResolve?.()
    this.sessionSwitchPromise = null
    this.sessionSwitchResolve = null
    console.log("[API] Session switch lock released")
  }

  /**
   * Unified context lifecycle switch — POST /context/switch.
   *
   * - Login bootstrap (isLogin: true): uses Cognito-only auth (no X-Context-Token).
   *   Concurrent login bootstraps are deduplicated via pendingSwitchContextLoginPromise.
   * - Sponsor/role switch (isLogin: false): uses full auth (Authorization + X-Context-Token).
   *   Wraps in markIntentionalContextReplacement() to suppress spurious 401 dialogs.
   */
  async switchContext({
    sponsorId,
    role,
    isLogin,
  }: {
    sponsorId: string
    role?: UserRole
    isLogin: boolean
  }): Promise<{ kind: "ok" } | GeneralApiProblem> {
    if (isLogin) {
      if (this.pendingSwitchContextLoginPromise) {
        console.log("[API] switchContext (login) already in flight — sharing existing promise")
        return this.pendingSwitchContextLoginPromise
      }

      console.log("[API] switchContext (login)", { sponsorId, role })

      const work = (async (): Promise<{ kind: "ok" } | GeneralApiProblem> => {
        try {
          const response: ApiResponse<ContextSwitchResponse> = await this.apisauce.post(
            "context/switch",
            { sponsor_id: sponsorId, is_login: true, ...(role ? { role } : {}) },
            {
              headers: {
                ...(await this.getCognitoOnlyHeaders()),
                "Content-Type": "application/json",
                Accept: "application/json",
              },
            },
          )

          if (!response.ok) {
            const problem = getGeneralApiProblem(response)
            if (problem) return problem
          }

          if (response.data?.context_token) {
            setContextToken(response.data.context_token)
          }

          return { kind: "ok" }
        } finally {
          this.pendingSwitchContextLoginPromise = null
        }
      })()

      this.pendingSwitchContextLoginPromise = work
      return work
    }

    console.log("[API] switchContext (switch)", { sponsorId, role })
    markIntentionalContextReplacement()
    const response: ApiResponse<ContextSwitchResponse> = await this.apisauce.post(
      "context/switch",
      { sponsor_id: sponsorId, is_login: false, ...(role ? { role } : {}) },
      {
        headers: {
          ...(await this.getHeaders()),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    if (response.data?.context_token) {
      setContextToken(response.data.context_token)
    }

    return { kind: "ok" }
  }

  /**
   * Silent context-token refresh — POST /context/refresh.
   *
   * Used by the silent-recovery handler (AuthContext.onContextTokenError) when a
   * CONTEXT_TOKEN_* 401 arrives. Sends both Authorization + X-Context-Token
   * (current/expired) so the backend can validate before reminting.
   *
   * The response transform exempts this endpoint from the recovery callback path,
   * preventing infinite loops.
   */
  async refreshContext(): Promise<{ kind: "ok" } | GeneralApiProblem> {
    console.log("[API] refreshContext")
    markIntentionalContextReplacement()
    const response: ApiResponse<ContextRefreshResponse> = await this.apisauce.post(
      "context/refresh",
      {},
      {
        headers: {
          ...(await this.getHeaders()),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    if (response.data?.context_token) {
      setContextToken(response.data.context_token)
    }

    return { kind: "ok" }
  }

  /**
   * Explicit displaced-tab reclaim — POST /context/reactivate.
   *
   * Used by the "Resume Here" path in SessionTakeoverDialog. Sends both
   * Authorization + X-Context-Token (current/expired).
   *
   * The response transform exempts this endpoint from recovery callbacks.
   */
  async reactivateContext(): Promise<{ kind: "ok" } | GeneralApiProblem> {
    console.log("[API] reactivateContext")
    markIntentionalContextReplacement()
    const response: ApiResponse<ContextReactivateResponse> = await this.apisauce.post(
      "context/reactivate",
      {},
      {
        headers: {
          ...(await this.getHeaders()),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    if (response.data?.context_token) {
      setContextToken(response.data.context_token)
    }

    return { kind: "ok" }
  }

  async getCurrentChannelBrand(): Promise<
    { kind: "ok"; data: ChannelBrandData } | GeneralApiProblem
  > {
    console.log("[API] getCurrentChannelBrand")
    const response: ApiResponse<ChannelBrandData> = await this.apisauce.get(
      "sponsors/current-sponsor",
      {},
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    if (!response.data) {
      return { kind: "bad-data", data: response }
    }

    // brand_image is returned as a signed path without a scheme/domain.
    // Build a new object (never mutate response.data) with the corrected URL.
    const raw = response.data
    const base = this.config.url.replace(/\/$/, "")
    const brandImage =
      raw.brand_image && !raw.brand_image.startsWith("http")
        ? `${base}/files/${raw.brand_image}`
        : raw.brand_image

    return { kind: "ok", data: { ...raw, brand_image: brandImage } }
  }

  /**
   * Session-validated probe (optional diagnostics / future use).
   */
  async validateSession(): Promise<
    { kind: "ok"; result: Types.SessionValidateResponse } | GeneralApiProblem
  > {
    console.log("[API] validateSession")
    const response: ApiResponse<Types.SessionValidateResponse> = await this.apisauce.get(
      "session/validate",
      {},
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    if (!response.data?.valid_session) {
      return { kind: "bad-data", data: response }
    }

    return { kind: "ok", result: response.data }
  }

  /**
   * Fetch the list of content resources associated with a task or step.
   *
   * Pass `taskId` for CM-level resources (from PlayVideo/PlayAudio `event_args.task_id`) or
   * `stepId` for step-level resources. The returned summaries contain `resource_id` values that
   * can be passed to `getContentResource` to obtain fresh signed `thumbnail_s3_key` /
   * `playlist_s3_key` URLs.
   */
  async getContentResources(
    params: { taskId: string; stepId?: undefined } | { stepId: string; taskId?: undefined },
  ): Promise<{ kind: "ok"; resources: ContentResourceSummary[] } | GeneralApiProblem> {
    const query =
      params.taskId !== undefined ? { task_id: params.taskId } : { step_id: params.stepId }
    const response: ApiResponse<ContentResourceSummary[]> = await this.apisauce.get(
      "content/get_resources",
      query,
      { headers: await this.getHeaders() },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    if (!response.data) {
      return { kind: "bad-data", data: response }
    }

    return { kind: "ok", resources: response.data }
  }
}
